import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { CachedImage } from '~/components/common/cached-image/cached-image';
import { Button } from '~/components/ui/button';

import type { Product } from '../../types/catalog-operations.types';

interface Props {
  product: Product;
  /** Attributes on this product that are proposed to deliver, vs. the total
   *  shown for it — computed by the caller from `computeProposedFieldStatus`. */
  proposedCount: number;
  totalCount: number;
  /** Step to the previous/next product in the rail (crossing pages). */
  onPrevious: () => void;
  onNext: () => void;
  hasPrevious: boolean;
  hasNext: boolean;
}

/**
 * Header strip for the selected product in the pre-delivery preview step:
 * identity plus the proposed-vs-total attribute tally. Simpler than
 * `DeliveryCompareProductStrip` — there is no real delivery verdict to show
 * yet, only the operator's proposed selection. The retailer id shows in
 * full here (unlike the rail's shortened id) and wraps rather than
 * truncating — it's the one place the full id is worth seeing.
 */
export function DeliveryWizardPreviewStrip({
  product,
  proposedCount,
  totalCount,
  onPrevious,
  onNext,
  hasPrevious,
  hasNext,
}: Props) {
  const { t } = useTranslation('catalog-operations');
  const { t: tCommon } = useTranslation('common');

  return (
    <div
      className="bg-background rounded-xl border p-4"
      data-testid="delivery-wizard-preview-strip"
    >
      <div className="flex min-w-0 items-start gap-4">
        <CachedImage
          src={product.imageUrl}
          alt={product.title}
          className="h-16 w-16 shrink-0 rounded-lg border"
        />
        <div className="min-w-0 flex-1">
          <h3
            className="text-foreground text-base leading-snug font-semibold"
            data-testid="delivery-wizard-preview-strip-title"
          >
            {product.title}
          </h3>
          <p
            className="text-muted-foreground mt-1 text-xs break-all"
            data-testid="delivery-wizard-preview-strip-source-id"
          >
            {t('deliveryCompare.strip.sourceId', { id: product.sourceProductId })}
          </p>
          <p className="text-sm">
            <span
              className="text-foreground font-medium"
              data-testid="delivery-wizard-preview-strip-tally-value"
            >
              {tCommon('deliveryWizard.preview.strip.tallyCount', {
                delivered: proposedCount,
                total: totalCount,
              })}
            </span>{' '}
            <span className="text-muted-foreground">
              {tCommon('deliveryWizard.preview.strip.tallySuffix')}
            </span>
          </p>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <Button
            type="button"
            variant="outline"
            size="icon"
            className="h-8 w-8 cursor-pointer disabled:cursor-not-allowed"
            aria-label={tCommon('deliveryWizard.preview.strip.previousProduct')}
            title={tCommon('deliveryWizard.preview.strip.previousProduct')}
            disabled={!hasPrevious}
            data-testid="delivery-wizard-preview-strip-previous-button"
            onClick={onPrevious}
          >
            <ChevronLeft className="size-4" aria-hidden="true" />
          </Button>
          <Button
            type="button"
            variant="outline"
            size="icon"
            className="h-8 w-8 cursor-pointer disabled:cursor-not-allowed"
            aria-label={tCommon('deliveryWizard.preview.strip.nextProduct')}
            title={tCommon('deliveryWizard.preview.strip.nextProduct')}
            disabled={!hasNext}
            data-testid="delivery-wizard-preview-strip-next-button"
            onClick={onNext}
          >
            <ChevronRight className="size-4" aria-hidden="true" />
          </Button>
        </div>
      </div>
    </div>
  );
}
